import type { orgchart } from '@cwos/api-client';

/**
 * COVERAGE IS DATED, AND "OVERLAPS THE WINDOW YOU ASKED ABOUT" IS NOT "IN FORCE
 * RIGHT NOW".
 *
 * ---------------------------------------------------------------------------
 * THE TRAP, NAMED
 * ---------------------------------------------------------------------------
 * `POST /orgchart/coverage/evaluate` answers a question about a WINDOW the
 * caller supplied. Its `covered` boolean is, verbatim:
 *
 *     const covered = assessment.coverage.length > 0;
 *
 * over rows selected by `starts_at < :to and ends_at > :from` — overlap with
 * the requested window, with `revoked_at is null`. So `covered: true` means
 * "at least one un-revoked coverage record touches the window you named". It
 * does NOT mean:
 *
 *   * that anything is in force at this moment (the window may be entirely in
 *     the future, or entirely in the past),
 *   * that the whole window is covered (a one-day record inside a five-day
 *     absence returns `covered: true` and leaves four days with nobody), or
 *   * that the delegate currently holds anything (that is
 *     `GET /orgchart/users/:id/effective-authority`, and only that).
 *
 * A screen that renders `covered` as a green "Covered" badge is telling an
 * administrator that authority has moved when it has not. It is the same family
 * as the defect the V2 gate caught three times in one week — *"a fixture that
 * assumes 'recent' means 'inside the current period' is green six days a
 * week"* — pointed at authority instead of at a metric.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS MODULE IS AND IS NOT ALLOWED TO DO
 * ---------------------------------------------------------------------------
 * Everything here is ARITHMETIC OVER DATES THE SERVER SENT — the standing of a
 * window against a clock, and the complement of a set of intervals. None of it
 * is a claim about authority. The screens are held to a harder rule than this
 * module can enforce on its own: *never render a computed authority the server
 * did not confirm*, which means "who holds what" is only ever read from
 * `effectiveAuthority`, and "what a delegation lent" is only ever the server's
 * own `inheritedKeys`. A window being in force says a record exists and is
 * dated to now; it does not say what that record resolved to.
 *
 * ---------------------------------------------------------------------------
 * UNPARSEABLE IS ITS OWN ANSWER
 * ---------------------------------------------------------------------------
 * `new Date('nonsense')` is `NaN`, and every comparison against `NaN` is
 * `false` — so a naive `ends <= now ? 'expired' : 'active'` calls an unreadable
 * end date ACTIVE. That is the failure pointed the wrong way: a record whose
 * expiry cannot be read would render as live authority. `undated` exists so the
 * screen can say the date is unreadable rather than guess in either direction.
 */

/** Where a dated window sits relative to a clock. */
export type CoverageStanding =
  /** `now < startsAt` — arranged, not yet conferring anything. */
  | 'scheduled'
  /** `startsAt <= now < endsAt` — the window the server resolves delegation from. */
  | 'in_force'
  /** `now >= endsAt` — lapsed. Nothing was revoked; it simply ended. */
  | 'expired'
  /** A boundary that would not parse. Never guessed into one of the other three. */
  | 'undated';

export interface DatedWindow {
  readonly startsAt: string;
  readonly endsAt: string;
}

export function coverageStanding(window: DatedWindow, now: Date): CoverageStanding {
  const starts = Date.parse(window.startsAt);
  const ends = Date.parse(window.endsAt);
  const at = now.getTime();
  if (!Number.isFinite(starts) || !Number.isFinite(ends) || !Number.isFinite(at)) return 'undated';
  // A zero-or-negative-length window cannot be in force: `starts_at <= now <
  // ends_at` is unsatisfiable for it, and the server's own resolution says the
  // same. Calling it `expired` matches what the server would answer.
  if (ends <= starts) return 'expired';
  if (at < starts) return 'scheduled';
  // STRICTLY LESS THAN, matching the server's `starts_at <= now() < ends_at`.
  // The instant of expiry belongs to `expired`, not to the last millisecond of
  // authority — an off-by-one here is an authority that outlives its window.
  if (at < ends) return 'in_force';
  return 'expired';
}

export const COVERAGE_STANDING_LABEL: Readonly<Record<CoverageStanding, string>> = Object.freeze({
  scheduled: 'Scheduled — not in force yet',
  in_force: 'In force now',
  expired: 'Expired',
  undated: 'Dates unreadable',
});

/**
 * The one-line explanation under a coverage row.
 *
 * Every branch says WHEN, because "until when" is half the fact about a
 * delegation and the half an administrator needs in order to stop worrying
 * about revoking something.
 */
export function coverageStandingSentence(
  window: DatedWindow,
  delegateName: string,
  now: Date,
): string {
  const standing = coverageStanding(window, now);
  switch (standing) {
    case 'scheduled':
      return `${delegateName} will cover from ${formatWhen(window.startsAt)} until ${formatWhen(window.endsAt)}. Nothing has moved yet — this window has not opened.`;
    case 'in_force':
      return `${delegateName} is covering until ${formatWhen(window.endsAt)}, when this ends on its own. Nothing has to be revoked.`;
    case 'expired':
      return `${delegateName}’s cover ended at ${formatWhen(window.endsAt)}. It is no longer conferring anything, and it was never written down as a grant — the server stopped resolving it at that instant.`;
    case 'undated':
      return `${delegateName} has a coverage record whose dates could not be read (${window.startsAt} → ${window.endsAt}). Treat it as unknown rather than as active or lapsed, and check the record itself.`;
  }
}

/** How this row should be toned. `undated` is `unsure`, never `good`. */
export function coverageStandingVariant(
  standing: CoverageStanding,
): 'good' | 'look' | 'plain' | 'unsure' {
  switch (standing) {
    case 'in_force':
      return 'good';
    case 'scheduled':
      return 'look';
    case 'expired':
      return 'plain';
    case 'undated':
      return 'unsure';
  }
}

export interface WindowGap {
  readonly from: string;
  readonly to: string;
}

/**
 * The parts of `window` that no coverage record touches.
 *
 * This is the fact `covered: boolean` cannot carry. A manager away Monday to
 * Friday with a delegate booked for Monday alone is `covered: true`, no
 * coverage-gap task is raised, and four days of their reports have nobody —
 * the exact shape of a silent authority hole.
 *
 * Rows with unreadable dates are EXCLUDED from the merge rather than treated as
 * covering nothing or as covering everything: an unreadable row is reported on
 * its own line by {@link coverageStandingSentence}, and letting it silently
 * shrink or widen a gap would hide it inside a derived number.
 */
export function uncoveredGaps(
  window: { readonly from: string; readonly to: string },
  rows: readonly DatedWindow[],
): WindowGap[] {
  const from = Date.parse(window.from);
  const to = Date.parse(window.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return [];

  const clipped: { start: number; end: number }[] = [];
  for (const row of rows) {
    const start = Date.parse(row.startsAt);
    const end = Date.parse(row.endsAt);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
    const s = Math.max(start, from);
    const e = Math.min(end, to);
    if (e > s) clipped.push({ start: s, end: e });
  }
  clipped.sort((a, b) => a.start - b.start);

  const gaps: WindowGap[] = [];
  let cursor = from;
  for (const interval of clipped) {
    if (interval.start > cursor) {
      gaps.push({
        from: new Date(cursor).toISOString(),
        to: new Date(interval.start).toISOString(),
      });
    }
    if (interval.end > cursor) cursor = interval.end;
  }
  if (cursor < to)
    gaps.push({ from: new Date(cursor).toISOString(), to: new Date(to).toISOString() });
  return gaps;
}

/**
 * What the assessment actually established, told apart from what its boolean
 * says.
 *
 * `serverCovered` is repeated back unchanged — this module never overrides the
 * server — and everything beside it is the narrowing the screen needs so the
 * boolean is not read as more than it is.
 */
export interface CoverageVerdict {
  /** The server's own `covered`, verbatim. */
  readonly serverCovered: boolean;
  /** Records the server returned for the requested window. */
  readonly overlapping: number;
  /** Of those, how many are dated to this instant. Often zero when `covered` is true. */
  readonly inForceNow: number;
  /** Records whose dates could not be read at all. */
  readonly undated: number;
  /** Parts of the requested window nobody covers. Empty means fully covered. */
  readonly gaps: readonly WindowGap[];
  /** The window the answer is about, echoed from the server's own reply. */
  readonly window: { readonly from: string; readonly to: string };
}

export function coverageVerdict(assessment: orgchart.CoverageNeedView, now: Date): CoverageVerdict {
  const standings = assessment.coverage.map((c) => coverageStanding(c, now));
  return {
    serverCovered: assessment.covered,
    overlapping: assessment.coverage.length,
    inForceNow: standings.filter((s) => s === 'in_force').length,
    undated: standings.filter((s) => s === 'undated').length,
    // The gap set is computed against the window the SERVER echoed, not the one
    // the form holds: the form can have moved on since the request was sent,
    // and a gap measured against a window the answer is not about is a fiction.
    gaps: uncoveredGaps({ from: assessment.from, to: assessment.to }, assessment.coverage),
    window: { from: assessment.from, to: assessment.to },
  };
}

/**
 * The headline sentence for an assessment. It NEVER says the bare word
 * "covered" without saying which window and whether anything is in force now.
 */
export function coverageVerdictSentence(verdict: CoverageVerdict, managerName: string): string {
  const window = `${formatWhen(verdict.window.from)} → ${formatWhen(verdict.window.to)}`;
  if (verdict.overlapping === 0) {
    return `Nobody covers ${managerName} at any point between ${window}. Work routed to them has nowhere to go for the whole of it.`;
  }
  const parts: string[] = [
    `${verdict.overlapping} coverage record${verdict.overlapping === 1 ? '' : 's'} touch${verdict.overlapping === 1 ? 'es' : ''} ${window}.`,
  ];
  parts.push(
    verdict.inForceNow === 0
      ? 'None of them is in force at this moment — this is an answer about that window, not about now.'
      : `${verdict.inForceNow} of them ${verdict.inForceNow === 1 ? 'is' : 'are'} in force at this moment.`,
  );
  if (verdict.undated > 0) {
    parts.push(
      `${verdict.undated} record${verdict.undated === 1 ? ' has dates that' : 's have dates that'} could not be read, so ${verdict.undated === 1 ? 'it is' : 'they are'} counted as neither.`,
    );
  }
  if (verdict.gaps.length > 0) {
    parts.push(
      `${verdict.gaps.length} part${verdict.gaps.length === 1 ? '' : 's'} of the window ${verdict.gaps.length === 1 ? 'is' : 'are'} still uncovered.`,
    );
  } else {
    parts.push('Every part of the window has somebody.');
  }
  return parts.join(' ');
}

/**
 * `Date#toLocaleString` with a stable shape.
 *
 * These are INSTANTS (`timestamptz` on the wire), unlike the calendar days a
 * relationship's `effective_from` carries — so local formatting is right here
 * and wrong there. `orgChartLogic.formatDate` handles the other case and the
 * two must not be swapped.
 */
export function formatWhen(iso: string): string {
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return iso;
  return new Date(at).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * An ISO instant for `<input type="datetime-local">`, in the BROWSER'S zone.
 *
 * `toISOString().slice(0, 16)` is the obvious spelling and is wrong: it renders
 * UTC into a control the browser reads as local, so an administrator west of
 * Greenwich types "09:00" and sends an instant several hours off. The offset
 * has to be subtracted before slicing.
 */
export function toLocalInputValue(when: Date): string {
  const local = new Date(when.getTime() - when.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/**
 * The reverse: a `datetime-local` value is a wall-clock time with no zone, and
 * the API wants an offset-bearing ISO instant (`z.string().datetime({ offset:
 * true })`). `new Date(value)` reads it in the browser's zone, which is what the
 * person meant, and `toISOString` then states the instant unambiguously.
 */
export function fromLocalInputValue(value: string): string | null {
  if (value === '') return null;
  const at = new Date(value);
  return Number.isFinite(at.getTime()) ? at.toISOString() : null;
}
